import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { BookOpen, Download, FileCode2, Printer, Loader2, ListTree, User, MessageSquare, Send, ChevronDown, HelpCircle, CircleDot, Circle, Ban, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import Seo from "@/components/Seo";
import api from "@/lib/api";

/* Programmer handover — renders /bridge/INTEGRATION_GUIDE.md (single source of
   truth) with: working section TOC, live assignable task cards backed by
   /api/guide/*, per-task + general comment threads, an FAQ accordion and an
   "ask the guide" box. Writes need the team code (owner holds it). */

const API_MD = "/bridge/INTEGRATION_GUIDE.md";
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const TASK_RE = /^- \[ \] \*\*([A-Z]+-\d{2}) · (.+?)\*\*(?: \(([^)]+)\))? — (.+)$/;
const STATUS_META = {
  todo: { label: "To do", Icon: Circle, cls: "text-zinc-500 border-white/15" },
  doing: { label: "Doing", Icon: CircleDot, cls: "text-amber-300 border-amber-500/50" },
  blocked: { label: "Blocked", Icon: Ban, cls: "text-red-400 border-red-500/50" },
  done: { label: "Done", Icon: CheckCircle2, cls: "text-emerald-400 border-emerald-500/50" },
};

function inline(text, keyPrefix = "") {
  const parts = [];
  let rest = text, k = 0;
  while (rest) {
    const m = rest.match(/\*\*(.+?)\*\*|`([^`]+)`/);
    if (!m) { parts.push(rest); break; }
    parts.push(rest.slice(0, m.index));
    if (m[1] !== undefined) parts.push(<strong key={`${keyPrefix}b${k++}`} className="text-zinc-100 font-semibold">{m[1]}</strong>);
    else parts.push(<code key={`${keyPrefix}c${k++}`} className="font-mono text-[0.85em] text-nc-orange bg-white/[0.06] px-1 py-0.5 rounded-sm">{m[2]}</code>);
    rest = rest.slice(m.index + m[0].length);
  }
  return parts;
}

/* ---------- live task card (assign / status / comments) ---------- */
function TaskCard({ id, title, est, desc, getLive, comments, onAct, name, code }) {
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState("");
  const live = getLive ? getLive(id) : null;
  const st = STATUS_META[live?.status || "todo"];
  const thread = comments.filter((c) => c.task_id === id);
  return (
    <li data-testid={`guide-task-${id}`} className="border border-white/10 bg-black/40 my-2 list-none">
      <div className="flex items-start gap-3 px-4 py-3">
        <button data-testid={`task-status-${id}`} title="Cycle status"
          onClick={() => onAct("status", id, { status: { todo: "doing", doing: "done", done: "todo", blocked: "todo" }[live?.status || "todo"] })}
          className={`mt-0.5 shrink-0 border rounded-sm p-1 transition-colors ${st.cls}`}>
          <st.Icon className="w-3.5 h-3.5" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[11.5px] text-zinc-200 leading-relaxed">
            <span className="text-nc-orange font-bold">{id}</span> · <strong className="text-zinc-50">{title}</strong>
            {est && <span className="text-zinc-500"> ({est})</span>} — {inline(desc, `d${id}`)}
          </p>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            {live?.assignee
              ? <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-nc-blue flex items-center gap-1"><User className="w-3 h-3" />{live.assignee}</span>
              : <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-zinc-600">unassigned</span>}
            <button data-testid={`task-assign-${id}`} onClick={() => onAct("assign", id, {})}
              className="font-mono text-[9px] uppercase tracking-[0.15em] border border-nc-blue/40 text-nc-blue px-2 py-0.5 hover:bg-nc-blue/10 transition-colors">
              {live?.assignee ? "Reassign" : "Assign me"}
            </button>
            {(["doing", "blocked", "done"]).map((s) => (
              <button key={s} data-testid={`task-mark-${s}-${id}`} onClick={() => onAct("status", id, { status: s })}
                className={`font-mono text-[9px] uppercase tracking-[0.15em] px-2 py-0.5 border transition-colors ${STATUS_META[s].cls} hover:bg-white/5`}>{s}</button>
            ))}
            <button data-testid={`task-comments-${id}`} onClick={() => setOpen((o) => !o)}
              className="font-mono text-[9px] uppercase tracking-[0.15em] text-zinc-500 hover:text-zinc-200 flex items-center gap-1 transition-colors">
              <MessageSquare className="w-3 h-3" />{thread.length} {open ? "▲" : "▼"}
            </button>
          </div>
        </div>
      </div>
      {open && (
        <div className="border-t border-white/10 px-4 py-3 flex flex-col gap-2">
          {thread.length === 0 && <p className="font-mono text-[10px] text-zinc-600">No comments yet — questions, blockers, API requests welcome.</p>}
          {thread.map((c) => (
            <p key={c.id} data-testid={`task-comment-${c.id.slice(0, 8)}`} className="font-mono text-[10.5px] text-zinc-400 leading-relaxed">
              <span className="text-nc-orange">{c.author}</span> <span className="text-zinc-600">· {new Date(c.created_at).toLocaleString()}</span><br />{c.body}
            </p>
          ))}
          <div className="flex gap-2 mt-1">
            <input data-testid={`task-comment-input-${id}`} value={comment} onChange={(e) => setComment(e.target.value)}
              placeholder={`Comment as ${name || "…"} (API-REQUEST: prefix for hub changes)`}
              className="flex-1 bg-black/60 border border-white/10 px-3 py-2 font-mono text-[10px] text-zinc-100 placeholder:text-zinc-700 focus:outline-none focus:border-nc-orange/50" />
            <button data-testid={`task-comment-send-${id}`} onClick={() => { if (comment.trim().length > 1) { onAct("comment", id, { body: comment.trim() }); setComment(""); } }}
              className="shrink-0 border border-nc-orange/50 text-nc-orange px-3 hover:bg-nc-orange/10 transition-colors"><Send className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      )}
    </li>
  );
}

/* ---------- markdown renderer (with task cards + FAQ accordions) ---------- */
function renderMd(md, taskProps) {
  const blocks = [];
  const lines = md.split("\n");
  let i = 0, key = 0, section = "";
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith("```")) {
      const buf = [];
      i++;
      while (i < lines.length && !lines[i].startsWith("```")) buf.push(lines[i++]);
      i++;
      blocks.push(<pre key={key++} data-testid="guide-code" className="bg-black/70 border border-white/10 p-4 overflow-x-auto font-mono text-[11px] leading-relaxed text-zinc-300 my-4">{buf.join("\n")}</pre>);
      continue;
    }
    if (/^#{1,3}\s/.test(line)) {
      const level = line.match(/^#+/)[0].length;
      const text = line.replace(/^#+\s*/, "");
      const id = slug(text);
      if (level === 1) section = id;
      const cls = level === 1
        ? "font-sans font-black uppercase tracking-tight text-2xl sm:text-3xl text-zinc-50 mt-12 mb-4 pb-3 border-b border-white/10 first:mt-0 scroll-mt-24"
        : level === 2
          ? "font-sans font-bold uppercase tracking-wide text-lg text-nc-blue mt-10 mb-3 scroll-mt-24"
          : "font-sans font-semibold text-base text-zinc-100 mt-7 mb-2 scroll-mt-24";
      blocks.push(level === 1
        ? <h2 key={key++} id={id} className={cls}>{inline(text)}</h2>
        : <h3 key={key++} id={id} className={cls}>{inline(text)}</h3>);
      i++; continue;
    }
    if (line.startsWith("|")) {
      const rows = [];
      while (i < lines.length && lines[i].startsWith("|")) rows.push(lines[i++]);
      const cells = (r) => r.split("|").slice(1, -1).map((c) => c.trim());
      const head = cells(rows[0]);
      const body = rows.slice(2);
      blocks.push(
        <div key={key++} className="overflow-x-auto my-4 border border-white/10">
          <table className="w-full font-mono text-[10.5px]">
            <thead><tr className="bg-white/[0.04]">{head.map((h, j) => <th key={j} className="text-left px-3 py-2 text-zinc-300 uppercase tracking-[0.1em] font-semibold border-b border-white/10">{h}</th>)}</tr></thead>
            <tbody>{body.map((r, ri) => (
              <tr key={ri} className="border-b border-white/5 last:border-0">{cells(r).map((c, ci) => <td key={ci} className="px-3 py-2 text-zinc-400 align-top">{inline(c, `t${ri}-${ci}`)}</td>)}</tr>
            ))}</tbody>
          </table>
        </div>);
      continue;
    }
    if (/^- \[ \] \*\*[A-Z]+-\d{2}/.test(line)) {
      // live task card
      const items = [];
      while (i < lines.length && /^- \[ \]/.test(lines[i])) items.push(lines[i++]);
      blocks.push(
        <ul key={key++} className="my-2 pl-0">
          {items.map((it, j) => {
            const m = it.match(TASK_RE);
            if (!m) return null;
            const [, id, title, est, desc] = m;
            return <TaskCard key={id} id={id} title={title} est={est} desc={desc} {...taskProps} />;
          })}
        </ul>);
      continue;
    }
    if (/^- /.test(line) || /^\d+\. /.test(line)) {
      const items = [];
      while (i < lines.length && (/^- /.test(lines[i]) || /^\d+\. /.test(lines[i]))) items.push(lines[i++]);
      blocks.push(
        <ul key={key++} className="flex flex-col gap-1.5 my-3">
          {items.map((it, j) => {
            const num = it.match(/^(\d+)\. /)?.[1];
            return (
              <li key={j} className="font-mono text-[11.5px] text-zinc-400 leading-relaxed flex gap-2.5 items-start">
                <span className="text-nc-orange shrink-0 font-bold">{num ? `${num}.` : "—"}</span>
                <span>{inline(it.replace(/^- /, "").replace(/^\d+\. /, ""), `li${j}`)}</span>
              </li>
            );
          })}
        </ul>);
      continue;
    }
    if (line.startsWith("---")) { blocks.push(<hr key={key++} className="border-white/10 my-8" />); i++; continue; }
    if (!line.trim()) { i++; continue; }
    const buf = [];
    while (i < lines.length && lines[i].trim() && !/^(#{1,3}\s|```|\||- |\d+\. |---)/.test(lines[i])) buf.push(lines[i++]);
    const text = buf.join(" ");
    const faqM = section.includes("faq") && text.match(/^\*\*(.+?\?)\*\*\s+(.+)$/s);
    if (faqM) {
      blocks.push(<FaqItem key={key++} q={faqM[1]} a={faqM[2]} idx={key} />);
    } else {
      blocks.push(<p key={key++} className="font-mono text-[11.5px] text-zinc-400 leading-relaxed my-3">{inline(text, `p${key}`)}</p>);
    }
  }
  return blocks;
}

function FaqItem({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`guide-faq-${idx}`} className="border border-white/10 bg-black/40 my-2">
      <button onClick={() => setOpen((o) => !o)} className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-white/[0.03] transition-colors">
        <HelpCircle className="w-4 h-4 text-nc-blue shrink-0" />
        <span className="font-mono text-[11.5px] text-zinc-200 flex-1">{q}</span>
        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="px-4 pb-3 pt-1 font-mono text-[11px] text-zinc-400 leading-relaxed border-t border-white/5">{inline(a, `fa${idx}`)}</p>}
    </div>
  );
}

/* ---------- ask the guide: keyword search over FAQ + section bodies ---------- */
function buildAskIndex(md) {
  const idx = [];
  let section = "", title = "", buf = [];
  const flush = () => { if (title) idx.push({ kind: "section", title, id: slug(title), body: buf.join(" ") }); };
  for (const line of md.split("\n")) {
    if (line.startsWith("# ")) { flush(); title = line.replace(/^#\s*/, ""); section = slug(title); buf = []; continue; }
    buf.push(line);
  }
  flush();
  for (const m of md.matchAll(/\*\*([^*\n]{8,140}?\?)\*\*[ \t]*([^\n]+)/g)) {
    idx.push({ kind: "faq", title: m[1], body: m[2] });
  }
  return idx;
}

function askGuide(index, query) {
  const terms = query.toLowerCase().split(/[^a-z0-9]+/).filter((t) => t.length > 2);
  if (!terms.length) return null;
  let best = null, bestScore = 0;
  for (const entry of index) {
    const hayTitle = entry.title.toLowerCase(), hayBody = entry.body.toLowerCase();
    let score = 0;
    for (const t of terms) {
      if (hayTitle.includes(t)) score += entry.kind === "faq" ? 4 : 3;
      if (hayBody.includes(t)) score += 1;
    }
    if (score > bestScore) { best = entry; bestScore = score; }
  }
  return bestScore >= 3 ? best : null;
}

export default function BridgeGuidePage() {
  const [md, setMd] = useState("");
  const [err, setErr] = useState("");
  const [state, setState] = useState({ tasks: {}, comments: [] });
  const [name, setName] = useState(() => localStorage.getItem("nc_guide_name") || "");
  const [code, setCode] = useState(() => localStorage.getItem("nc_guide_code") || "");
  const [askQ, setAskQ] = useState("");
  const [askA, setAskA] = useState(null);
  const [genComment, setGenComment] = useState("");
  const askIndex = useMemo(() => (md ? buildAskIndex(md) : []), [md]);

  const loadState = () => api.get("/guide/state").then((r) => setState(r.data)).catch(() => {});
  useEffect(() => {
    fetch(API_MD).then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.text(); }).then(setMd).catch((e) => setErr(e.message));
    loadState();
  }, []);

  const toc = useMemo(() => md.split("\n").filter((l) => /^#\s+\d+\./.test(l)).map((l) => l.replace(/^#+\s*/, "")), [md]);
  const taskIds = useMemo(() => [...md.matchAll(/^- \[ \] \*\*([A-Z]+-\d{2})/gm)].map((m) => m[1]), [md]);
  const doneCount = taskIds.filter((id) => state.tasks[id]?.status === "done").length;

  const onAct = async (kind, taskId, extra) => {
    if (!name.trim()) { toast.error("Set your name first (top of the guide board)"); return; }
    if (!code.trim()) { toast.error("Team code required — ask the project owner"); return; }
    localStorage.setItem("nc_guide_name", name.trim());
    localStorage.setItem("nc_guide_code", code.trim());
    try {
      if (kind === "assign") await api.post(`/guide/tasks/${taskId}/assign`, { name: name.trim(), code: code.trim() });
      else if (kind === "status") await api.post(`/guide/tasks/${taskId}/status`, { status: extra.status, name: name.trim(), code: code.trim() });
      else if (kind === "comment") await api.post("/guide/comments", { task_id: taskId, author: name.trim(), body: extra.body, code: code.trim() });
      toast.success("Saved");
      loadState();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const ask = () => {
    const hit = askGuide(askIndex, askQ);
    setAskA(hit || { kind: "none", title: "No good match", body: "Try different keywords, check the FAQ section, or leave a comment at the bottom — the owner reads the board." });
  };

  // render with per-task live state injected via getLive
  const contentWithLive = useMemo(() => {
    if (!md) return null;
    return renderMd(md, {
      comments: state.comments, onAct, name, code,
      getLive: (id) => state.tasks[id],
    });
  }, [md, state, name, code]);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="bridge-guide-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col">
      <Seo title="Full Game Integration Guide — New Citizen" description="The complete programmer handover: bridge setup, MySQL contract, in-engine UE5 work items, police missions, and the go-live checklist — with live task assignment and comments." />
      <SiteNav />

      <header className="border-b border-white/10 relative overflow-hidden print:hidden">
        <div className="absolute inset-0 grid-texture opacity-40" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-14 md:py-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="font-mono text-[10px] tracking-[0.4em] text-nc-orange uppercase mb-3 flex items-center gap-2"><BookOpen className="w-4 h-4" /> Programmer handover · target ship Oct 25 2026</p>
            <h1 className="font-sans font-black uppercase tracking-tight text-3xl sm:text-5xl text-zinc-50 leading-[1.05]">Full Game Integration Guide</h1>
            <p className="font-mono text-xs sm:text-sm text-zinc-400 mt-4 max-w-2xl leading-relaxed">
              Everything your programmers need — bridge, MySQL contract, in-engine UE5 work, the police mission
              system, FAQ — with live task assignment and comments so the team can self-organize.
            </p>
            <div className="mt-7 flex gap-3 flex-wrap">
              <a data-testid="guide-download-md" href={API_MD} download
                className="bg-nc-orange hover:bg-nc-orange/80 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-6 py-3 inline-flex items-center gap-2 transition-colors"
                style={{ boxShadow: "0 0 24px rgba(255,107,26,0.25)" }}>
                <Download className="w-4 h-4" /> Guide (.md)
              </a>
              <a data-testid="guide-download-bridge" href="/bridge/nc_bridge.py" download
                className="border border-white/20 hover:border-white/50 font-sans font-semibold uppercase tracking-[0.2em] text-xs px-6 py-3 inline-flex items-center gap-2 transition-colors">
                <FileCode2 className="w-4 h-4 text-nc-blue" /> nc_bridge.py
              </a>
              <button data-testid="guide-print" onClick={() => window.print()}
                className="border border-white/20 hover:border-white/50 font-sans font-semibold uppercase tracking-[0.2em] text-xs px-6 py-3 inline-flex items-center gap-2 transition-colors">
                <Printer className="w-4 h-4" /> Print / PDF
              </button>
            </div>

            {/* team board bar */}
            <div data-testid="guide-team-bar" className="mt-8 border border-white/10 bg-black/50 p-4 flex items-center gap-3 flex-wrap">
              <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Team board:</span>
              <input data-testid="guide-dev-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="your name"
                className="bg-black/60 border border-white/10 px-3 py-2 font-mono text-[10px] text-zinc-100 placeholder:text-zinc-700 focus:outline-none focus:border-nc-blue/50 w-36" />
              <input data-testid="guide-team-code" type="password" value={code} onChange={(e) => setCode(e.target.value)} placeholder="team code"
                className="bg-black/60 border border-white/10 px-3 py-2 font-mono text-[10px] text-zinc-100 placeholder:text-zinc-700 focus:outline-none focus:border-nc-blue/50 w-32" />
              <span data-testid="guide-progress" className="font-mono text-[10px] uppercase tracking-[0.15em] text-emerald-400 ml-auto">
                {doneCount} / {taskIds.length} tasks done
              </span>
            </div>
          </motion.div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10 w-full flex-1 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-10">
        <aside className="hidden lg:block print:hidden">
          <div className="sticky top-24 flex flex-col gap-6">
            <div>
              <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-zinc-500 mb-3 flex items-center gap-2"><ListTree className="w-3.5 h-3.5" /> Sections</p>
              <nav className="flex flex-col gap-0.5 max-h-[50vh] overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
                {toc.map((t) => (
                  <button key={t} data-testid={`guide-toc-${slug(t)}`}
                    onClick={() => document.getElementById(slug(t))?.scrollIntoView({ behavior: "smooth", block: "start" })}
                    className="text-left font-mono text-[10px] text-zinc-500 hover:text-zinc-100 transition-colors py-1.5 border-l-2 border-white/10 hover:border-nc-orange pl-3">
                    {t}
                  </button>
                ))}
              </nav>
            </div>
            {/* ask the guide */}
            <div className="border border-white/10 bg-black/50 p-3.5">
              <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-zinc-500 mb-2 flex items-center gap-1.5"><HelpCircle className="w-3.5 h-3.5 text-nc-orange" /> Ask the guide</p>
              <input data-testid="guide-ask" value={askQ} onChange={(e) => setAskQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && ask()}
                placeholder="e.g. how do arrests reach court?"
                className="w-full bg-black/60 border border-white/10 px-3 py-2 font-mono text-[10px] text-zinc-100 placeholder:text-zinc-700 focus:outline-none focus:border-nc-orange/50" />
              <button data-testid="guide-ask-btn" onClick={ask}
                className="mt-2 w-full bg-nc-orange/90 hover:bg-nc-orange text-black font-mono text-[9px] uppercase tracking-[0.2em] font-bold py-2 transition-colors">Answer</button>
              {askA && (
                <div data-testid="guide-ask-answer" className="mt-3 border-t border-white/10 pt-3">
                  <p className="font-mono text-[10px] text-nc-blue mb-1">{askA.title}</p>
                  <p className="font-mono text-[10px] text-zinc-400 leading-relaxed">{askA.body.slice(0, 320)}{askA.body.length > 320 ? "…" : ""}</p>
                  {askA.kind === "section" && (
                    <button onClick={() => document.getElementById(askA.id)?.scrollIntoView({ behavior: "smooth" })}
                      className="mt-2 font-mono text-[9px] uppercase tracking-[0.15em] text-nc-orange hover:underline">Jump to section →</button>
                  )}
                </div>
              )}
            </div>
          </div>
        </aside>

        <div className="min-w-0">
          <article data-testid="guide-content" className="border border-white/10 bg-[#070707] p-6 sm:p-10">
            {err && <p className="font-mono text-xs text-red-400">Failed to load the guide: {err}</p>}
            {!md && !err && <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>}
            {contentWithLive}
          </article>

          {/* general comments thread */}
          <section data-testid="guide-general-comments" className="mt-8 border border-white/10 bg-[#070707] p-6 sm:p-8 print:hidden">
            <p className="font-sans font-bold uppercase text-lg text-zinc-50 mb-4 flex items-center gap-2"><MessageSquare className="w-5 h-5 text-nc-orange" /> Team discussion</p>
            <div className="flex flex-col gap-3 mb-4 max-h-80 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
              {state.comments.filter((c) => c.task_id === "general").length === 0 && (
                <p className="font-mono text-[10px] text-zinc-600">Nothing yet. Questions, suggestions, and API-REQUESTs land here — the owner reads this board.</p>
              )}
              {state.comments.filter((c) => c.task_id === "general").map((c) => (
                <p key={c.id} data-testid={`guide-comment-${c.id.slice(0, 8)}`} className="font-mono text-[11px] text-zinc-400 leading-relaxed border-l-2 border-white/10 pl-3">
                  <span className="text-nc-orange">{c.author}</span> <span className="text-zinc-600">· {new Date(c.created_at).toLocaleString()}</span><br />{c.body}
                </p>
              ))}
            </div>
            <div className="flex gap-2">
              <input data-testid="guide-general-input" value={genComment} onChange={(e) => setGenComment(e.target.value)}
                placeholder={`Comment as ${name || "…"} — set your name + team code above`}
                className="flex-1 bg-black/60 border border-white/10 px-3 py-2.5 font-mono text-[11px] text-zinc-100 placeholder:text-zinc-700 focus:outline-none focus:border-nc-orange/50" />
              <button data-testid="guide-general-send" onClick={() => { if (genComment.trim().length > 1) { onAct("comment", "general", { body: genComment.trim() }); setGenComment(""); } }}
                className="shrink-0 border border-nc-orange/50 text-nc-orange px-4 hover:bg-nc-orange/10 transition-colors"><Send className="w-4 h-4" /></button>
            </div>
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
